<template>
  <!--
    DsModal — componente app-local (el DS no expone un modal element). Construido
    sobre tokens del DS: scrim (--scrim-bg), panel en --surface-elevated, radius-md.
    v-model:open, cierra con Escape + backdrop. Teleport a body. Header (title+close)
    + body (slot) + footer (slot #footer, para acciones).
  -->
  <Teleport to="body">
    <Transition name="ds-modal">
      <div
        v-if="modelValue"
        class="ds-modal__overlay"
        @click.self="onBackdrop"
      >
        <div
          class="ds-modal__panel"
          :class="size !== 'md' && `ds-modal__panel--${size}`"
          role="dialog"
          aria-modal="true"
          :aria-label="title || undefined"
        >
          <header v-if="title || $slots.header || closable" class="ds-modal__head">
            <slot name="header">
              <h2 v-if="title" class="ds-modal__title">{{ title }}</h2>
            </slot>
            <button v-if="closable" type="button" class="ds-modal__close" aria-label="Cerrar" @click="close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </header>
          <div class="ds-modal__body"><slot /></div>
          <footer v-if="$slots.footer" class="ds-modal__foot"><slot name="footer" /></footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'DsModal',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    size: { type: String, default: 'md', validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v) },
    closable: { type: Boolean, default: true },
    closeOnBackdrop: { type: Boolean, default: true },
  },
  emits: ['update:modelValue', 'close'],
  watch: {
    modelValue(open) {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : '';
      }
    },
  },
  mounted() {
    this._onKey = (e) => { if (e.key === 'Escape' && this.modelValue) this.close(); };
    window.addEventListener('keydown', this._onKey);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this._onKey);
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    onBackdrop() {
      if (this.closeOnBackdrop) this.close();
    },
  },
};
</script>

<style scoped lang="scss">
.ds-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background: color-mix(in srgb, var(--scrim-bg) 62%, transparent);
}
.ds-modal__panel {
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - var(--space-12));
  display: flex;
  flex-direction: column;
  background: var(--surface-elevated);
  color: var(--text-default);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  overflow: hidden;
  font-family: var(--font-sans);
}
.ds-modal__panel--sm { max-width: 360px; }
.ds-modal__panel--lg { max-width: 720px; }
.ds-modal__panel--xl { max-width: 1040px; }

.ds-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--container-pad-m) var(--container-pad-m) var(--space-4);
}
.ds-modal__title {
  font-family: var(--font-sans);
  font-size: var(--text-h3-size);
  font-weight: 500;
  line-height: var(--text-h3-line);
  letter-spacing: var(--text-h3-track);
  color: var(--text-default);
  margin: 0;
}
.ds-modal__close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: calc(var(--space-2) * -1) calc(var(--space-2) * -1) 0 0;
  border: 0;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--duration-base, 240ms) var(--ease-default, ease),
    background var(--duration-base, 240ms) var(--ease-default, ease);
  svg { width: 24px; height: 24px; }  // consistente con el close de los drawers (PhX 24).
}
@media (hover: hover) and (pointer: fine) {
  .ds-modal__close:hover {
    color: var(--text-default);
    background: color-mix(in srgb, var(--text-default) 6%, transparent);
  }
}
.ds-modal__close:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 1px;
}

.ds-modal__body {
  padding: 0 var(--container-pad-m);
  overflow-y: auto;
  color: var(--text-muted);
  font-size: var(--text-body-size);
  line-height: var(--text-body-line);
}
.ds-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-6) var(--container-pad-m) var(--container-pad-m);
}

/* Transición */
.ds-modal-enter-active,
.ds-modal-leave-active { transition: opacity var(--duration-base, 240ms) var(--ease-default, ease); }
.ds-modal-enter-from,
.ds-modal-leave-to { opacity: 0; }
.ds-modal-enter-active .ds-modal__panel,
.ds-modal-leave-active .ds-modal__panel { transition: transform var(--duration-base, 240ms) var(--ease-default, ease); }
.ds-modal-enter-from .ds-modal__panel,
.ds-modal-leave-to .ds-modal__panel { transform: translateY(8px); }

@media (prefers-reduced-motion: reduce) {
  .ds-modal-enter-active, .ds-modal-leave-active,
  .ds-modal-enter-active .ds-modal__panel, .ds-modal-leave-active .ds-modal__panel { transition: none; }
}
</style>
